import { motion } from 'framer-motion'
import { useState } from 'react'

interface FormData {
  name: string
  email: string
  phone: string
  subject: string
  message: string
}

interface SubmitMessage {
  type: 'success' | 'error'
  message: string
}

export default function Contact() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState<SubmitMessage | null>(null)

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage(null)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(
          result.message || `HTTP error! status: ${response.status}`
        )
      }

      if (!result.success) {
        throw new Error(result.message || 'Form submission failed')
      }

      // Success
      setSubmitMessage({
        type: 'success',
        message: 'Vielen Dank für deine Nachricht! Wir melden uns so schnell wie möglich bei dir.',
      })

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Etwas ist schief gelaufen. Bitte versuche es später erneut.'
      setSubmitMessage({
        type: 'error',
        message: errorMessage,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section
      id="contact"
      className="min-h-dvh pt-24 pb-12 md:py-20 px-6 snap-start snap-always"
      style={{ backgroundColor: '#101216' }}
    >
      <div className="max-w-4xl mx-auto flex flex-col justify-center md:min-h-[calc(100dvh-96px)]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 md:mb-12"
        >
          <h2
            className="text-3xl md:text-headline mb-4 md:mb-6"
            style={{
              color: '#ffffff',
              textShadow: '0 0 30px rgba(173, 174, 167, 0.6), 0 0 60px rgba(173, 174, 167, 0.3)'
            }}
          >
            Kontakt aufnehmen
          </h2>
          <p className="text-sm md:text-body text-apple-gray-400 max-w-2xl mx-auto">
            Schreib uns, was du dir vorstellst – wir entwickeln einen maßgeschneiderten Effekt oder geben dir eine kostenlose Erstberatung.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="bg-apple-gray-50 rounded-3xl p-6 md:p-10"
        >
          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-apple-gray-700 mb-2"
                >
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2.5 md:py-3 rounded-xl bg-white border border-apple-gray-200 text-apple-gray-900 placeholder-apple-gray-400 focus:outline-none focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent transition-all duration-300"
                  placeholder="Dein Name"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-apple-gray-700 mb-2"
                >
                  E-Mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2.5 md:py-3 rounded-xl bg-white border border-apple-gray-200 text-apple-gray-900 placeholder-apple-gray-400 focus:outline-none focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent transition-all duration-300"
                  placeholder="deine@email.de"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-apple-gray-700 mb-2"
              >
                Telefon  (Optional)
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl bg-white border border-apple-gray-200 text-apple-gray-900 placeholder-apple-gray-400 focus:outline-none focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent transition-all duration-300"
                placeholder="+49 123 4567890"
              />
            </div>

            <div>
              <label
                htmlFor="subject"
                className="block text-sm font-medium text-apple-gray-700 mb-2"
              >
                Betreff *
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-xl bg-white border border-apple-gray-200 text-apple-gray-900 placeholder-apple-gray-400 focus:outline-none focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent transition-all duration-300"
                placeholder="Worum geht es?"
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium text-apple-gray-700 mb-2"
              >
                Nachricht *
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                value={formData.message}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-xl bg-white border border-apple-gray-200 text-apple-gray-900 placeholder-apple-gray-400 focus:outline-none focus:ring-2 focus:ring-apple-gray-900 focus:border-transparent transition-all duration-300 resize-none"
                placeholder="Schreib uns, was du dir vorstellst..."
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full px-6 py-4 rounded-full bg-apple-gray-900 text-white font-medium transition-all duration-300 ${isSubmitting
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-apple-gray-800 hover:scale-[1.02]'
                  }`}
              >
                {isSubmitting ? 'Senden...' : 'Nachricht senden'}
              </button>
            </div>
          </form>

          {submitMessage && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-6 p-4 rounded-xl ${submitMessage.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
                }`}
            >
              {submitMessage.message}
            </motion.div>
          )}
        </motion.div>

        {/* Direct Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-6 md:mt-10 text-center"
        >
          <p className="text-sm text-apple-gray-400 mb-4">Oder kontaktiere uns direkt:</p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <a
              href="mailto:info@verticalfx.de"
              className="text-white hover:text-apple-gray-300 transition-colors duration-300 font-medium"
            >
              info@verticalfx.de
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
